import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  Badge,
  Button,
  Alert,
  Loader,
  ActionIcon,
  Paper,
  Box,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconAlertCircle,
  IconPencil,
  IconDownload,
} from '@tabler/icons-react';
import { useTank } from '@/contexts/TankContext';
import { NoTankSelected } from '@/components/common/NoTankSelected';
import { useLivestockDetail } from '@/hooks/useLivestock';
import { useTimeline } from '@/hooks/useTimeline';
import { TimelineFeed } from '@/features/livestock/TimelineFeed';
import { AddTimelineEntry } from '@/features/livestock/AddTimelineEntry';
import { EditLivestockModal, EditTimelineEntryModal, ShareableImageGrid } from '@/features/livestock';
import { CompareButton } from '@/features/livestock/CompareButton';
import { OrganismPhotoViewer } from '@/features/livestock/OrganismPhotoViewer';
import { ORGANISM_WORDS } from '@/features/livestock/photoSubject';
import { sharedLinkWarning } from '@/features/livestock/shareLinkActions';
import { SharedLinksSection } from '@/features/livestock/SharedLinksSection';
import { getStoredImageSize, setStoredImageSize } from '@/features/livestock/imageSize';
import type { ImageSize } from '@/features/livestock/imageSize';
import type { TimelineEntryUpdate } from '@/types/timeline';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useMigrateNotes } from '@/hooks/useMigrateNotes';
import type { TimelineEntry } from '@/types/timeline';

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'alive':
      return 'green';
    case 'deceased':
      return 'red';
    case 'sold':
      return 'blue';
    default:
      return 'gray';
  }
};

function formatDate(dateString: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatPrice(price: number | null, currency: string): string {
  if (price === null) return '';
  const symbols: Record<string, string> = {
    USD: '$',
    GBP: '\u00a3',
    EUR: '\u20ac',
    AUD: 'A$',
    CAD: 'C$',
  };
  return `${symbols[currency] || currency}${price.toFixed(2)}`;
}

export function OrganismDetailPage(): JSX.Element {
  const { livestockId } = useParams<{
    tankId: string;
    livestockId: string;
  }>();
  const navigate = useNavigate();
  const { selectedTank } = useTank();
  const isMobile = useIsMobile();

  const livestockIdNum = livestockId ? parseInt(livestockId, 10) : null;

  // Lazy migration: convert legacy notes field to timeline entry on first view
  const { data: migrateResult } = useMigrateNotes(livestockIdNum ?? undefined);

  const {
    data: livestock,
    isLoading: isLoadingLivestock,
    error: livestockError,
  } = useLivestockDetail(livestockIdNum);

  const {
    entries,
    isLoading: isLoadingTimeline,
    hasMore,
    loadMore,
    updateEntry,
    deleteEntry,
    isUpdating,
  } = useTimeline(livestockIdNum ?? undefined);

  const [editModalOpened, setEditModalOpened] = useState(false);
  const [gridModalOpened, setGridModalOpened] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimelineEntry | null>(null);
  const [imageSize, setImageSize] = useState<ImageSize>(getStoredImageSize);
  const handleImageSizeChange = useCallback((size: ImageSize) => {
    setImageSize(size);
    setStoredImageSize(size);
  }, []);

  const photoCount = useMemo(
    () => entries.filter((e) => e.photo_display_url || e.photo_url).length,
    [entries],
  );
  const hasMultiplePhotos = photoCount >= 2;
  // The photo open in the viewer, read from the entries so a line up's refetch shows in it
  const [viewingId, setViewingId] = useState<number | null>(null);
  const viewing = useMemo(
    () => (viewingId === null ? null : entries.find((e) => e.id === viewingId) ?? null),
    [entries, viewingId],
  );
  const handleOpenPhoto = useCallback((entry: TimelineEntry) => setViewingId(entry.id), []);
  // The open photo was deleted (or left the timeline): the viewer closes, so it
  // can't open again by itself if the entry comes back (as TankShotsPage does).
  useEffect(() => {
    if (viewingId !== null && !isLoadingTimeline && !viewing) setViewingId(null);
  }, [viewingId, viewing, isLoadingTimeline]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleEditEntry = useCallback((entry: TimelineEntry) => {
    setEditingEntry(entry);
  }, []);

  const handleSaveEntry = useCallback(
    async (entryId: number, data: TimelineEntryUpdate) => {
      try {
        await updateEntry({ entryId, data });
        setEditingEntry(null);
      } catch {
        // Notification already shown by useTimeline onError callback.
        // mutateAsync re-throws, so we catch here to prevent unhandled rejection.
      }
    },
    [updateEntry],
  );

  const handleDeleteEntry = useCallback(
    async (entryId: number) => {
      // A photo that public links show: they stop working (livestock compare decision 13)
      const shared = entries.find((e) => e.id === entryId)?.share_link_count ?? 0;
      const question =
        shared > 0
          ? `Delete this timeline entry? ${sharedLinkWarning(shared)}`
          : 'Delete this timeline entry?';
      if (confirm(question)) {
        await deleteEntry(entryId);
      }
    },
    [deleteEntry, entries],
  );

  if (!selectedTank) {
    return (
      <Container size="lg" py="xl">
        <NoTankSelected purpose="to view this organism" />
      </Container>
    );
  }

  if (isLoadingLivestock) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </Container>
    );
  }

  if (livestockError || !livestock) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {(livestockError as Error)?.message || 'Organism not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        {/* Back button */}
        <Button
          variant="subtle"
          color="gray"
          size="sm"
          leftSection={<IconArrowLeft size={16} />}
          onClick={handleBack}
          style={{ alignSelf: 'flex-start' }}
        >
          Back
        </Button>

        {/* Header section */}
        <Box>
          <Group justify="space-between" align="flex-start">
            <Box>
              <Title
                order={2}
                style={{
                  fontSize: isMobile ? 22 : 28,
                }}
              >
                {livestock.common_name}
              </Title>
              {livestock.scientific_name && (
                <Text size="sm" c="dimmed" fs="italic">
                  {livestock.scientific_name}
                </Text>
              )}
            </Box>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              onClick={() => setEditModalOpened(true)}
              title="Edit organism details"
            >
              <IconPencil size={16} />
            </ActionIcon>
          </Group>

          <Group gap="xs" mt="xs">
            {livestock.status !== 'unknown' && (
              <Badge
                color={getStatusColor(livestock.status)}
                variant="filled"
                size="sm"
              >
                {livestock.status.toUpperCase()}
              </Badge>
            )}
            {livestock.category && (
              <Badge
                variant="light"
                color="teal"
                size="xs"
                tt="uppercase"
                style={{
                  fontSize: 10,
                  letterSpacing: '0.05em',
                  fontWeight: 600,
                }}
              >
                {livestock.category}
              </Badge>
            )}
            {livestock.subcategory && (
              <Badge
                variant="light"
                color="cyan"
                size="xs"
                tt="uppercase"
                style={{
                  fontSize: 10,
                  letterSpacing: '0.05em',
                  fontWeight: 600,
                }}
              >
                {livestock.subcategory}
              </Badge>
            )}
          </Group>

          {/* Metadata line */}
          <Text size="sm" c="dimmed" mt="xs">
            {['Acquired: ' + formatDate(livestock.acquisition_date),
              livestock.vendor,
              livestock.price_paid !== null
                ? formatPrice(livestock.price_paid, livestock.currency)
                : null,
            ]
              .filter(Boolean)
              .join(' \u00b7 ')}
          </Text>

          {/* Legacy notes - hidden once migrated to timeline or when timeline entries exist */}
          {livestock.notes && !migrateResult?.migrated && entries.length === 0 && (
            <Paper
              withBorder
              radius="md"
              p="sm"
              mt="sm"
              style={{
                backgroundColor: 'var(--mantine-color-gray-0)',
              }}
            >
              <Text size="xs" fw={500} c="dimmed" mb={4}>
                Notes
              </Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {livestock.notes}
              </Text>
            </Paper>
          )}
        </Box>

        {/* Add note form */}
        <AddTimelineEntry key={livestockIdNum} livestockId={livestockIdNum!} livestockType={livestock.livestock_type} />

        {/* Timeline section */}
        <Box>
          <Group justify="space-between" align="center" mb="sm">
            <Text
              fw={600}
              style={{
                fontFamily: 'Space Grotesk, sans-serif',
                fontSize: 18,
              }}
            >
              Timeline
            </Text>
            {!isLoadingTimeline && (
              <Group gap="xs" wrap="nowrap">
                <CompareButton
                  // With more entries still to load, two photos may be among them:
                  // Compare stays on, and the compare page says if there aren't.
                  total={hasMore ? Math.max(photoCount, 2) : photoCount}
                  words={ORGANISM_WORDS}
                  to={`/tanks/${encodeURIComponent(selectedTank.id)}/livestock/${livestock.id}/compare`}
                />
                {hasMultiplePhotos && (
                  <Tooltip label="Save photo grid">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size={isMobile ? 44 : 36}
                      onClick={() => setGridModalOpened(true)}
                      aria-label="Save photo grid"
                    >
                      <IconDownload size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            )}
          </Group>

          {isLoadingTimeline ? (
            <Group justify="center" py="xl">
              <Loader size="sm" />
            </Group>
          ) : (
            <TimelineFeed
              tankId={selectedTank.id}
              entries={entries}
              hasMore={hasMore}
              onLoadMore={loadMore}
              onEdit={handleEditEntry}
              onDelete={handleDeleteEntry}
              onOpenPhoto={handleOpenPhoto}
              acquisitionDate={livestock.acquisition_date}
              imageSize={imageSize}
              onImageSizeChange={handleImageSizeChange}
            />
          )}
        </Box>

        {/* This organism's public links (nothing shows without any) */}
        <SharedLinksSection tankId={selectedTank.id} livestockId={livestock.id} />
      </Stack>

      {/* Edit livestock modal */}
      <EditLivestockModal
        opened={editModalOpened}
        onClose={() => setEditModalOpened(false)}
        livestock={livestock}
      />

      {/* Edit timeline entry modal */}
      <EditTimelineEntryModal
        entry={editingEntry}
        opened={!!editingEntry}
        onClose={() => setEditingEntry(null)}
        onSave={handleSaveEntry}
        isSaving={isUpdating}
      />

      {/* A photo full screen, with Line up and Make reference */}
      <OrganismPhotoViewer
        tankId={selectedTank.id}
        livestock={livestock}
        entry={viewing}
        onClose={() => setViewingId(null)}
      />

      {/* Shareable photo grid modal */}
      <ShareableImageGrid
        opened={gridModalOpened}
        onClose={() => setGridModalOpened(false)}
        entries={entries}
        organismName={livestock.common_name}
        category={livestock.category}
        subcategory={livestock.subcategory}
        vendorName={livestock.vendor}
      />
    </Container>
  );
}
